/**
 * @cfleet/api-client - Inbox Management Hooks
 *
 * React Query hooks for inbox message operations with optimistic updates.
 * These endpoints require authentication.
 *
 * Inbox provides messages and notifications for the current user,
 * including system alerts, user messages, and starred items.
 *
 * @example
 * ```tsx
 * import {
 *   useInboxMessages,
 *   useMarkAsRead,
 *   useToggleStar,
 *   inboxQueryKeys,
 * } from '@cfleet/api-client';
 *
 * // List unread messages
 * const { data } = useInboxMessages({ filter: 'unread' }, { authConfig });
 *
 * // Mark a message as read (optimistic)
 * const { mutate: markAsRead } = useMarkAsRead({ authConfig });
 * markAsRead({ id: 'msg-001' });
 *
 * // Toggle star (optimistic)
 * const { mutate: toggleStar } = useToggleStar({ authConfig });
 * toggleStar({ id: 'msg-001' });
 * ```
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AnalyticsComponents as InboxComponents } from '../generated';
import { createAuthenticatedCoreClient, type AuthMiddlewareConfig } from '../index';

// ─────────────────────────────────────────────────────────────────────────────
// Type Aliases
// ─────────────────────────────────────────────────────────────────────────────

type InboxMessage = InboxComponents['schemas']['InboxMessage'];
type MessageSender = InboxComponents['schemas']['MessageSender'];
type InboxMessagesResponse = InboxComponents['schemas']['InboxMessagesResponse'];

// Re-export types for consumers
export type { InboxMessage, MessageSender, InboxMessagesResponse };

// ─────────────────────────────────────────────────────────────────────────────
// Configuration
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Configuration for inbox hooks
 */
export interface UseInboxHooksConfig {
  /** Authentication middleware configuration (required for authenticated endpoints) */
  authConfig: AuthMiddlewareConfig;
}

/**
 * Parameters for listing inbox messages
 */
export interface ListInboxMessagesParams {
  /** Filter messages by status */
  filter?: 'all' | 'unread' | 'starred';
  /** Filter by message type */
  type?: 'system' | 'user' | 'alert';
  /** Maximum messages to return */
  limit?: number;
  /** Cursor for pagination */
  cursor?: string;
}

/**
 * Query keys for inbox-related queries
 */
export const inboxQueryKeys = {
  all: ['inbox'] as const,
  messages: (params?: ListInboxMessagesParams) =>
    [...inboxQueryKeys.all, 'messages', params] as const,
  message: (id: string) => [...inboxQueryKeys.all, 'message', id] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// useInboxMessages
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to list inbox messages for the current user.
 *
 * Requires authentication. Results are scoped to the authenticated user.
 * Response includes `unread_count` for badge display.
 *
 * @param params - Optional filter and pagination parameters
 * @param config - Configuration including auth config
 * @returns Query hook with inbox messages and unread count
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useInboxMessages(
 *   { filter: 'unread' },
 *   { authConfig }
 * );
 *
 * return (
 *   <InboxList
 *     messages={data?.messages ?? []}
 *     unreadCount={data?.unread_count ?? 0}
 *   />
 * );
 * ```
 */
export function useInboxMessages(params?: ListInboxMessagesParams, config?: UseInboxHooksConfig) {
  return useQuery({
    queryKey: inboxQueryKeys.messages(params),
    queryFn: async (): Promise<InboxMessagesResponse> => {
      if (!config?.authConfig) {
        throw new Error('Auth config required');
      }

      const client = createAuthenticatedCoreClient(config.authConfig);
      const { data, error } = await client.GET('/inbox/messages', {
        params: { query: params },
      });

      if (error) {
        throw new Error(error.detail || 'Failed to fetch inbox messages');
      }

      return data!;
    },
    enabled: !!config?.authConfig,
    staleTime: 30_000,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useMarkAsRead
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to mark an inbox message as read.
 *
 * Uses optimistic updates: immediately sets the message to read in the local
 * cache, rolls back on error, and invalidates all inbox queries on success.
 *
 * @param config - Configuration including auth config
 * @returns Mutation hook for marking messages as read
 *
 * @example
 * ```tsx
 * const { mutate: markAsRead, isPending } = useMarkAsRead({ authConfig });
 *
 * markAsRead(
 *   { id: 'msg-001' },
 *   { onSuccess: (msg) => console.log(`Message ${msg.id} marked as read`) }
 * );
 * ```
 */
export function useMarkAsRead(config?: UseInboxHooksConfig) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string }): Promise<InboxMessage> => {
      if (!config?.authConfig) {
        throw new Error('Auth config required');
      }

      const client = createAuthenticatedCoreClient(config.authConfig);
      const { data, error } = await client.POST('/inbox/messages/{id}/read', {
        params: { path: { id } },
      });

      if (error) {
        throw new Error(error.detail || 'Failed to mark message as read');
      }

      return data!;
    },
    onMutate: async ({ id }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: inboxQueryKeys.all });

      // Snapshot previous inbox queries for rollback
      const previousQueries = queryClient.getQueriesData<InboxMessagesResponse>({
        queryKey: inboxQueryKeys.all,
      });

      // Optimistically update all inbox message lists. Only decrement
      // unread_count when this cache entry actually contains the target
      // message and that message is still unread — avoids undershoot on
      // filtered caches and on concurrent mark-reads from other tabs.
      queryClient.setQueriesData<InboxMessagesResponse>({ queryKey: inboxQueryKeys.all }, (old) => {
        if (!old) return old;
        const target = old.messages.find((msg) => msg.id === id);
        const shouldDecrement = !!target && target.unread !== false;
        return {
          ...old,
          unread_count: shouldDecrement ? Math.max(0, old.unread_count - 1) : old.unread_count,
          messages: old.messages.map((msg) =>
            msg.id === id ? { ...msg, unread: false, read_at: new Date().toISOString() } : msg
          ),
        };
      });

      return { previousQueries };
    },
    onError: (_err, _vars, context) => {
      // Roll back to previous state on error
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inboxQueryKeys.all });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useToggleStar
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to toggle the starred status of an inbox message.
 *
 * Uses optimistic updates: immediately toggles the star in the local cache,
 * rolls back on error, and invalidates all inbox queries on success.
 *
 * @param config - Configuration including auth config
 * @returns Mutation hook for toggling message star
 *
 * @example
 * ```tsx
 * const { mutate: toggleStar, isPending } = useToggleStar({ authConfig });
 *
 * toggleStar(
 *   { id: 'msg-001' },
 *   { onSuccess: (msg) => console.log(`Star toggled: ${msg.starred}`) }
 * );
 * ```
 */
export function useToggleStar(config?: UseInboxHooksConfig) {
  const queryClient = useQueryClient();

  return useMutation({
    // Toggle is non-idempotent (SET starred = NOT starred). A retry after
    // a network drop that actually delivered the first request would flip
    // the star back. Disable retry here; the global QueryClient default is 1.
    retry: 0,
    mutationFn: async ({ id }: { id: string }): Promise<InboxMessage> => {
      if (!config?.authConfig) {
        throw new Error('Auth config required');
      }

      const client = createAuthenticatedCoreClient(config.authConfig);
      const { data, error } = await client.POST('/inbox/messages/{id}/star', {
        params: { path: { id } },
      });

      if (error) {
        throw new Error(error.detail || 'Failed to toggle message star');
      }

      return data!;
    },
    onMutate: async ({ id }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: inboxQueryKeys.all });

      // Snapshot previous inbox queries for rollback
      const previousQueries = queryClient.getQueriesData<InboxMessagesResponse>({
        queryKey: inboxQueryKeys.all,
      });

      // Optimistically toggle star on all inbox message lists
      queryClient.setQueriesData<InboxMessagesResponse>({ queryKey: inboxQueryKeys.all }, (old) => {
        if (!old) return old;
        return {
          ...old,
          messages: old.messages.map((msg) =>
            msg.id === id ? { ...msg, starred: !msg.starred } : msg
          ),
        };
      });

      return { previousQueries };
    },
    onError: (_err, _vars, context) => {
      // Roll back to previous state on error
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inboxQueryKeys.all });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useResolveMessage
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to resolve a warning/critical inbox message.
 *
 * Only applicable to messages with severity "warning" or "critical".
 * The server returns 422 for info-severity messages.
 */
export function useResolveMessage(config?: UseInboxHooksConfig) {
  const queryClient = useQueryClient();

  return useMutation({
    // These mutations are non-idempotent (each call creates a new state transition).
    // A retry after a network drop that delivered the first request would cause a
    // confusing 422 response. Disable retry here; the global QueryClient default is 1.
    retry: 0,
    mutationFn: async ({ id, note }: { id: string; note?: string }): Promise<InboxMessage> => {
      if (!config?.authConfig) throw new Error('Auth config required');
      const client = createAuthenticatedCoreClient(config.authConfig);
      const { data, error } = await client.POST('/inbox/messages/{id}/resolve', {
        params: { path: { id } },
        body: note !== undefined ? { note } : undefined,
      });
      if (error) throw new Error(error.detail || 'Failed to resolve message');
      return data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inboxQueryKeys.all });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useIgnoreMessage
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to dismiss an inbox message without resolving it.
 */
export function useIgnoreMessage(config?: UseInboxHooksConfig) {
  const queryClient = useQueryClient();

  return useMutation({
    retry: 0,
    mutationFn: async ({ id, note }: { id: string; note?: string }): Promise<InboxMessage> => {
      if (!config?.authConfig) throw new Error('Auth config required');
      const client = createAuthenticatedCoreClient(config.authConfig);
      const { data, error } = await client.POST('/inbox/messages/{id}/ignore', {
        params: { path: { id } },
        body: note !== undefined ? { note } : undefined,
      });
      if (error) throw new Error(error.detail || 'Failed to ignore message');
      return data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inboxQueryKeys.all });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// usePostponeMessage
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to snooze an inbox message until a future date/time.
 *
 * The server enforces that remind_at must be at least 1 minute in the future.
 */
export function usePostponeMessage(config?: UseInboxHooksConfig) {
  const queryClient = useQueryClient();

  return useMutation({
    retry: 0,
    mutationFn: async ({
      id,
      remindAt,
      note,
    }: {
      id: string;
      remindAt: string;
      note?: string;
    }): Promise<InboxMessage> => {
      if (!config?.authConfig) throw new Error('Auth config required');
      const client = createAuthenticatedCoreClient(config.authConfig);
      const { data, error } = await client.POST('/inbox/messages/{id}/postpone', {
        params: { path: { id } },
        body: { remind_at: remindAt, ...(note !== undefined && { note }) },
      });
      if (error) throw new Error(error.detail || 'Failed to postpone message');
      return data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inboxQueryKeys.all });
    },
  });
}
